// Common
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { Component, inject } from '@angular/core';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';

// Types
import { Application } from '@modules/common/types/application';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { VirtualFolder } from '@modules/sheets/types/virtual-folder';

// Services
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { StitchType } from '@sharedlib/common/types/StitchType';

@Component({
  selector: 'app-sidebar-sheet-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less',
    './sidebar-context-menu.component.less',
  ],
  standalone: false,
})
export class SidebarContextMenuComponent extends BaseSidebarContextMenuComponent<
  StitchType.SHEET,
  StitchType.SHEET_FOLDER,
  VirtualFolder
> {
  protected readonly followUpWording = injectFollowUpWording();
  advancedSearchStateKey = Application.sheets;
  /** #1130: Empty Trash destroys, so it needs `purge` on the space the list is in. */
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly currentUserStore = inject(CurrentUserStore);

  /** The space the list is in: a workbook lives in the current space (Sheets has no team tenants yet). */
  protected get purgeTenantId(): string | null | undefined {
    return this.spaceContextStore.contextId() ?? this.currentUserStore.accountId();
  }

  constructor(sheetsService: SheetsService, sheetFoldersService: SheetFoldersService) {
    super(sheetsService, sheetFoldersService);
  }

  protected getCommonParams(): Like<Sheet | SheetFolder> {
    let params: Like<Sheet | SheetFolder> = {};

    switch (this.sidebarFilterKey) {
      case 'all_sheets':
      case 'all_sheetFolders':
      case 'templates': // Nothing is added there: a template is only ever saved from a workbook.
        break;
      case 'followed':
      case 'archived':
      case 'deleted':
      case 'snoozed':
      case 'flagged':
        params = super.getCommonParams();
        break;
      default:
        // The LOOSE check, deliberately. `BaseVirtualFolder` includes `'search_results'` and this
        // switch does not handle it, so `assertExhaustive` here is a build error -- which is how
        // this was found, once the typecheck gate was discovered to be checking nothing (B-53).
        //
        // Not fixed here because what `search_results` should put in `getCommonParams` is the same
        // question in five other sidebar menus that share this shape, and answering it for Sheets
        // alone would be a guess dressed as a decision. See B-14.
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  protected stitchItemFactory(params: Like<Sheet>): Sheet {
    return new Sheet({ ...params, sheetFolderId: this.selectedContainerStitchItemId });
  }

  protected containerStitchItemFactory(params: Like<SheetFolder>): SheetFolder {
    return new SheetFolder({ ...params, parentId: this.selectedContainerStitchItemId });
  }
}
